import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faSearch, 
  faClock, 
  faTasks, 
  faMapMarkerAlt, 
  faUser, 
  faFileAlt, 
  faFolder, 
  faImage, 
  faChevronRight, 
  faPlus, 
  faEdit, 
  faTrash, 
  faEye, 
  faDownload,
  faUpload,
  faCog,
  faList,
  faBuilding,
  faHardHat,
  faSpinner,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons'
import { useDocumentViewer } from '../contexts/DocumentViewerContext'
import SiteFileUploadModal from '../components/SiteFileUploadModal'
import { useAuth } from '../contexts/AuthContext'

interface ProjectsPageProps {
}

// Extend Site interface for tasks and hours
interface Task {
  id: string
  name: string
  completed: boolean
  subtasks?: Task[]
}

interface Site {
  id: string
  client: string
  siteName: string
  projectType: string
  state: string
  crewLead: string
  location: string
  documents: Document[]
  tasks?: Task[]
  totalHours?: number
}

interface Document {
  id: string
  name: string
  type: 'file' | 'folder'
  path: string
  children?: Document[]
}

// View state types
type ViewState =
  | { view: 'selection'; tab: 'projects' | 'management' }
  | { view: 'siteView'; siteId: string }

const ProjectsPage: React.FC<ProjectsPageProps> = () => {
  const navigate = useNavigate()
  const { openDocument } = useDocumentViewer();
  const { loading: authLoading, hasPermission } = useAuth()
  
  // Permission guard - redirect unauthorized users
  useEffect(() => {
    if (!authLoading && !hasPermission('projects')) {
      navigate('/', { replace: true });
    }
  }, [authLoading, hasPermission, navigate]);

  // Show loading while checking permissions
  if (authLoading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-primary animate-spin" />
          <span className="text-lg">Loading...</span>
        </div>
      </div>
    );
  }

  // Don't render anything if user doesn't have permission (will redirect)
  if (!hasPermission('projects')) {
    return null;
  }

  const [view, setView] = useState<ViewState>({ view: 'selection', tab: 'projects' })
  const [loading, setLoading] = useState(false)
  const [sites, setSites] = useState<Site[]>([])
  const [selectedSite, setSelectedSite] = useState<Site | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [totalHoursDisplay, setTotalHoursDisplay] = useState<number | undefined>(undefined);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadModalFile, setUploadModalFile] = useState<Document | null>(null);
  
  // Template management state
  const [templateTab, setTemplateTab] = useState<'newJob' | 'templateBuilder'>('newJob');

  const news = [
    'LTS wins new telecom contract',
    'Quarterly safety training announced',
    'Employee of the Month: Jane Doe',
  ]

  const recentActivity = [
    'User John submitted a new form',
    'Site 12 updated by Admin',
    'GIS data refreshed',
    'Resource "Safety Manual" added',
  ]

  // Helper functions to safely access ViewState properties
  const getCurrentTab = (): 'projects' | 'management' => {
    return view.view === 'selection' ? view.tab : 'projects'
  }

  const getManagementAction = (): 'newJob' | 'templateBuilder' | undefined => {
    return view.view === 'selection' && view.tab === 'management' ? templateTab : undefined
  }

  // Mock data for demonstration
  const mockSites: Site[] = [
    {
      id: '1',
      client: 'AT&T',
      siteName: 'Downtown Tower A',
      projectType: 'Tower Installation',
      state: 'TX',
      crewLead: 'John Smith',
      location: 'Austin, TX',
      totalHours: 128,
      tasks: [
        {
          id: 't1',
          name: 'Site Survey',
          completed: true,
          subtasks: [
            { id: 't1-1', name: 'Initial Walk', completed: true },
            { id: 't1-2', name: 'Photos', completed: true },
            { id: 't1-3', name: 'Survey Report', completed: true },
          ]
        },
        {
          id: 't2',
          name: 'Permitting',
          completed: false,
          subtasks: [
            { id: 't2-1', name: 'Building Permit', completed: true },
            { id: 't2-2', name: 'Electrical Permit', completed: false },
            { id: 't2-3', name: 'Environmental Review', completed: false },
          ]
        },
        {
          id: 't3',
          name: 'Construction',
          completed: false,
          subtasks: [
            { id: 't3-1', name: 'Foundation', completed: true },
            { id: 't3-2', name: 'Tower Erection', completed: false },
            { id: 't3-3', name: 'Electrical', completed: false },
            { id: 't3-4', name: 'Grounding', completed: false },
          ]
        },
        {
          id: 't4',
          name: 'Closeout',
          completed: false,
          subtasks: [
            { id: 't4-1', name: 'Final Walkthrough', completed: false },
            { id: 't4-2', name: 'Documentation', completed: false },
            { id: 't4-3', name: 'Punch List', completed: false },
          ]
        },
        {
          id: 't5',
          name: 'Commissioning',
          completed: false,
          subtasks: [
            { id: 't5-1', name: 'System Test', completed: false },
            { id: 't5-2', name: 'Owner Training', completed: false },
          ]
        }
      ],
      documents: [
        {
          id: '1',
          name: 'Permits',
          type: 'folder',
          path: '/documents/permits',
          children: [
            {
              id: '1-1',
              name: 'Building Permit.pdf',
              type: 'file',
              path: '/documents/permits/building-permit.pdf'
            },
            {
              id: '1-2',
              name: 'Electrical Permit_missing.pdf',
              type: 'file',
              path: '/documents/permits/electrical-permit_missing.pdf'
            }
          ]
        },
        {
          id: '2',
          name: 'Photos',
          type: 'folder',
          path: '/documents/photos',
          children: [
            {
              id: '2-1',
              name: 'Site Overview.jpg',
              type: 'file',
              path: '/documents/photos/site-overview.jpg'
            },
            {
              id: '2-2',
              name: 'Equipment Setup.jpg',
              type: 'file',
              path: '/documents/photos/equipment-setup.jpg'
            },
            {
              id: '2-3',
              name: 'Final Walkthrough_missing.png',
              type: 'file',
              path: '/documents/photos/final-walkthrough_missing.png'
            }
          ]
        },
        {
          id: '3',
          name: 'Drawings',
          type: 'folder',
          path: '/documents/drawings',
          children: [
            {
              id: '3-1',
              name: 'As-Built.pdf',
              type: 'file',
              path: '/documents/drawings/as-built.pdf'
            },
            {
              id: '3-2',
              name: 'Site Plan_missing.pdf',
              type: 'file',
              path: '/documents/drawings/site-plan_missing.pdf'
            }
          ]
        },
        {
          id: '4',
          name: 'Safety',
          type: 'folder',
          path: '/documents/safety',
          children: [
            {
              id: '4-1',
              name: 'JSA.pdf',
              type: 'file',
              path: '/documents/safety/jsa.pdf'
            },
            {
              id: '4-2',
              name: 'Safety Meeting Minutes_missing.txt',
              type: 'file',
              path: '/documents/safety/safety-meeting-minutes_missing.txt'
            }
          ]
        },
        {
          id: '5',
          name: 'Reports',
          type: 'folder',
          path: '/documents/reports',
          children: [
            {
              id: '5-1',
              name: 'Daily Report.pdf',
              type: 'file',
              path: '/documents/reports/daily-report.pdf'
            },
            {
              id: '5-2',
              name: 'Final Report_missing.pdf',
              type: 'file',
              path: '/documents/reports/final-report_missing.pdf'
            }
          ]
        },
        {
          id: '6',
          name: 'Warranties',
          type: 'folder',
          path: '/documents/warranties',
          children: [
            {
              id: '6-1',
              name: 'Roof Warranty.pdf',
              type: 'file',
              path: '/documents/warranties/roof-warranty.pdf'
            }
          ]
        },
        {
          id: '7',
          name: 'Submittals',
          type: 'folder',
          path: '/documents/submittals',
          children: [
            {
              id: '7-1',
              name: 'Material Submittal.pdf',
              type: 'file',
              path: '/documents/submittals/material-submittal.pdf'
            },
            {
              id: '7-2',
              name: 'Closeout Checklist_missing.txt',
              type: 'file',
              path: '/documents/submittals/closeout-checklist_missing.txt'
            }
          ]
        }
      ]
    },
    {
      id: '2',
      client: 'Verizon',
      siteName: 'Suburban Complex B',
      projectType: 'DAS Installation',
      state: 'CA',
      crewLead: 'Sarah Johnson',
      location: 'San Diego, CA',
      totalHours: 92,
      tasks: [
        {
          id: 't1',
          name: 'Site Walk',
          completed: true,
          subtasks: [
            { id: 't1-1', name: 'Initial Photos', completed: true },
            { id: 't1-2', name: 'Notes', completed: true }
          ]
        },
        {
          id: 't2',
          name: 'Permitting',
          completed: false,
          subtasks: [
            { id: 't2-1', name: 'Fire Permit', completed: true },
            { id: 't2-2', name: 'Electrical Permit_missing', completed: false }
          ]
        },
        {
          id: 't3',
          name: 'Installation',
          completed: false,
          subtasks: [
            { id: 't3-1', name: 'Rack Mount', completed: false },
            { id: 't3-2', name: 'Cabling', completed: false }
          ]
        }
      ],
      documents: [
        {
          id: '1',
          name: 'Permits',
          type: 'folder',
          path: '/documents/permits',
          children: [
            { id: '1-1', name: 'Fire Permit.pdf', type: 'file', path: '/documents/permits/fire-permit.pdf' },
            { id: '1-2', name: 'Electrical Permit_missing.pdf', type: 'file', path: '/documents/permits/electrical-permit_missing.pdf' }
          ]
        },
        {
          id: '2',
          name: 'Photos',
          type: 'folder',
          path: '/documents/photos',
          children: [
            { id: '2-1', name: 'Before.jpg', type: 'file', path: '/documents/photos/before.jpg' },
            { id: '2-2', name: 'After_missing.jpg', type: 'file', path: '/documents/photos/after_missing.jpg' }
          ]
        },
        {
          id: '3',
          name: 'Drawings',
          type: 'folder',
          path: '/documents/drawings',
          children: [
            { id: '3-1', name: 'Wiring Diagram.pdf', type: 'file', path: '/documents/drawings/wiring-diagram.pdf' }
          ]
        },
        {
          id: '4',
          name: 'Reports',
          type: 'folder',
          path: '/documents/reports',
          children: [
            { id: '4-1', name: 'Daily Report.pdf', type: 'file', path: '/documents/reports/daily-report.pdf' },
            { id: '4-2', name: 'Final Report_missing.pdf', type: 'file', path: '/documents/reports/final-report_missing.pdf' }
          ]
        },
        {
          id: '5',
          name: 'Submittals',
          type: 'folder',
          path: '/documents/submittals',
          children: [
            { id: '5-1', name: 'Material Submittal.pdf', type: 'file', path: '/documents/submittals/material-submittal.pdf' },
            { id: '5-2', name: 'Checklist_missing.txt', type: 'file', path: '/documents/submittals/checklist_missing.txt' }
          ]
        }
      ]
    },
    {
      id: '3',
      client: 'AT&T',
      siteName: 'Mall Parking Lot C',
      projectType: 'Small Cell Installation',
      state: 'TX',
      crewLead: 'Mike Wilson',
      location: 'Houston, TX',
      documents: []
    },
    {
      id: '4',
      client: 'T-Mobile',
      siteName: 'Office Building D',
      projectType: 'DAS Installation',
      state: 'NY',
      crewLead: 'Lisa Chen',
      location: 'New York, NY',
      documents: []
    },
    {
      id: '5',
      client: 'Verizon',
      siteName: 'Highway Tower E',
      projectType: 'Tower Installation',
      state: 'FL',
      crewLead: 'David Rodriguez',
      location: 'Miami, FL',
      documents: []
    },
    {
      id: '6',
      client: 'AT&T',
      siteName: 'Shopping Center F',
      projectType: 'Small Cell Installation',
      state: 'CA',
      crewLead: 'Jennifer Lee',
      location: 'Los Angeles, CA',
      documents: []
    },
    {
      id: '7',
      client: 'T-Mobile',
      siteName: 'Hospital Campus G',
      projectType: 'DAS Installation',
      state: 'IL',
      crewLead: 'Robert Brown',
      location: 'Chicago, IL',
      documents: []
    },
    {
      id: '8',
      client: 'Verizon',
      siteName: 'University Tower H',
      projectType: 'Tower Installation',
      state: 'MA',
      crewLead: 'Amanda Davis',
      location: 'Boston, MA',
      documents: []
    },
    {
      id: '9',
      client: 'AT&T',
      siteName: 'Airport Terminal I',
      projectType: 'DAS Installation',
      state: 'GA',
      crewLead: 'Thomas Miller',
      location: 'Atlanta, GA',
      documents: []
    },
    {
      id: '10',
      client: 'T-Mobile',
      siteName: 'Stadium Complex J',
      projectType: 'DAS Installation',
      state: 'TX',
      crewLead: 'Jessica Garcia',
      location: 'Dallas, TX',
      documents: []
    }
  ]

  // Load sites on component mount
  useEffect(() => {
    const loadSites = async () => {
      setLoading(true)
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      setSites(mockSites)
      setLoading(false)
    }

    loadSites()
  }, [])

  // Filter sites based on search
  const filteredSites = sites.filter(site => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return (
      site.client.toLowerCase().includes(term) ||
      site.siteName.toLowerCase().includes(term) ||
      site.projectType.toLowerCase().includes(term) ||
      site.crewLead.toLowerCase().includes(term) ||
      site.location.toLowerCase().includes(term)
    )
  })

  // Group sites by client
  const sitesByClient = filteredSites.reduce((acc, site) => {
    if (!acc[site.client]) {
      acc[site.client] = []
    }
    acc[site.client].push(site)
    return acc
  }, {} as Record<string, Site[]>)

  // Handle site selection
  const handleSiteSelect = (site: Site) => {
    setSelectedSite(site)
    setView({ view: 'siteView', siteId: site.id })
  }

  // Handle back navigation
  const handleBack = () => {
    setView({ view: 'selection', tab: 'projects' })
    setSelectedSite(null)
  }

  // Handle tab change
  const handleTabChange = (tab: 'projects' | 'management') => {
    setView({ view: 'selection', tab })
  }

  // Handle template navigation
  const handleTemplatesClick = () => {
    setView({ view: 'selection', tab: 'management' })
    setTemplateTab('newJob')
  }

  // Handle template tab change
  const handleTemplateTabChange = (tab: 'newJob' | 'templateBuilder') => {
    setTemplateTab(tab)
  }

  // Handler for upload modal
  const handleOpenUploadModal = (file: Document) => {
    setUploadModalFile(file);
    setUploadModalOpen(true);
  };
  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
    setUploadModalFile(null);
  };
  const handleUpload = (files: File[], markComplete: boolean) => {
    // Stub: just log for now
    console.log('Uploaded files:', files, 'Mark complete:', markComplete, 'For:', uploadModalFile);
  };



  // Site breadcrumb component
  const SiteBreadcrumb: React.FC<{ 
    client: string;
    siteName: string;
    onRootClick: () => void;
    onClientClick: (client: string) => void;
  }> = ({ client, siteName, onRootClick, onClientClick }) => {
    return (
      <div className="flex justify-center mb-8">
        <div className="breadcrumbs text-base">
          <ul>
            <li>
              <a className="flex items-center gap-1" onClick={onRootClick} style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faBuilding} className="h-4 w-4" />
                <span>Projects</span>
              </a>
            </li>
            <li>
              <a className="flex items-center gap-1" onClick={() => onClientClick(client)} style={{ cursor: 'pointer' }}>
                <span>{client}</span>
              </a>
            </li>
            <li>
              <span className="inline-flex items-center gap-1">
                {siteName}
              </span>
            </li>
          </ul>
        </div>
      </div>
    )
  }

  // File explorer component
  const FileExplorer: React.FC<{ documents: Document[] }> = ({ documents }) => {
    const renderDocument = (doc: Document) => {
      if (doc.type === 'folder') {
        return (
          <li key={doc.id}>
            <details open>
              <summary>
                <FontAwesomeIcon icon={faFolder} className="h-4 w-4" />
                {doc.name}
              </summary>
              {doc.children && (
                <ul>
                  {doc.children.map(child => renderDocument(child))}
                </ul>
              )}
            </details>
          </li>
        )
      } else {
        const isImage = doc.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|svg)$/)
        const isPdf = doc.name.toLowerCase().endsWith('.pdf')
        const isMissing = /_missing\.[a-z0-9]+$/i.test(doc.name)
        
        let icon = faFileAlt
        if (isImage) icon = faImage
        else if (isPdf) icon = faFileAlt
        if (isMissing) icon = faExclamationTriangle

        const handleClick = (e: React.MouseEvent) => {
          if (isMissing) {
            e.preventDefault();
            handleOpenUploadModal(doc);
          } else {
            e.preventDefault();
            openDocument(doc.name.replace(/_missing/i, ''), isPdf ? 'PDF' : isImage ? 'Image' : 'File')
          }
        }

        return (
          <li key={doc.id}>
            <a
              className={
                (isMissing
                  ? 'text-error font-semibold flex items-center gap-2 cursor-pointer hover:bg-warning/10 rounded transition-colors w-full'
                  : 'flex items-center gap-2 cursor-pointer hover:bg-primary/10 rounded transition-colors w-full')
              }
              onClick={handleClick}
              tabIndex={0}
              role={'button'}
            >
              <FontAwesomeIcon icon={icon} className={isMissing ? 'h-4 w-4 text-error' : 'h-4 w-4'} />
              {doc.name.replace(/_missing/i, '')}
              {isMissing && <span className="badge badge-error badge-sm ml-2">Missing</span>}
            </a>
          </li>
        )
      }
    }

    return (
      <ul className="menu menu-xs bg-base-200 rounded-box w-full text-lg lg:text-base">
        {documents.map(doc => renderDocument(doc))}
      </ul>
    )
  }

  // Helper to calculate % complete for a site
  function getSiteCompletion(site: Site): number {
    if (!site.tasks || site.tasks.length === 0) return 0
    let total = 0
    let complete = 0
    site.tasks.forEach(task => {
      if (task.subtasks && task.subtasks.length > 0) {
        total += task.subtasks.length
        complete += task.subtasks.filter(st => st.completed).length
      } else {
        total += 1
        if (task.completed) complete += 1
      }
    })
    return total === 0 ? 0 : Math.round((complete / total) * 100)
  }

  useEffect(() => {
    if (selectedSite) {
      const total = typeof selectedSite.totalHours === 'number' ? selectedSite.totalHours : 0;
      setTotalHoursDisplay(total - 1);
      let current = total - 1;
      const interval = setInterval(() => {
        if (current < total) {
          current++;
          setTotalHoursDisplay(current);
        } else {
          clearInterval(interval);
        }
      }, 80);
      return () => clearInterval(interval);
    }
  }, [selectedSite]);

  // Helper to get all missing files across all sites
  function getAllMissingFiles(sites: Site[]) {
    const missingFiles: { siteName: string; file: Document }[] = [];
    sites.forEach(site => {
      function traverse(docs: Document[]) {
        docs.forEach(doc => {
          if (doc.type === 'folder' && doc.children) {
            traverse(doc.children);
          } else if (doc.type === 'file' && /_missing\.[a-z0-9]+$/i.test(doc.name)) {
            missingFiles.push({ siteName: site.siteName, file: doc });
          }
        });
      }
      traverse(site.documents);
    });
    return missingFiles;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-base-100 flex flex-col">
        <Navbar pageTitle="PROJECTS" news={news} recentActivity={recentActivity} />
        <main className="container mx-auto px-4 py-8 flex-1 flex items-center justify-center">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="fa-spin text-4xl text-primary mb-4" />
            <p className="text-lg">Loading sites...</p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-base-100 flex flex-col">
      <Navbar pageTitle="PROJECTS" news={news} recentActivity={recentActivity} />
      <main className="container mx-auto px-4 py-8 flex-1">
        
        {/* Selection Container */}
        <div className={view.view === 'selection' ? '' : 'hidden'}>
          {/* Tabs */}
          <div className="tabs tabs-lift mb-4">
            <input
              type="radio"
              name="projects_tabs"
              role="tab"
              className="tab"
              aria-label="Projects"
              checked={getCurrentTab() === 'projects'}
              onChange={() => handleTabChange('projects')}
            />
            {hasPermission('projectsManager') && (
              <input
                type="radio"
                name="projects_tabs"
                role="tab"
                className="tab"
                aria-label="Project Management"
                checked={getCurrentTab() === 'management'}
                onChange={() => handleTabChange('management')}
              />
            )}
          </div>
          
          {/* Projects Tab */}
          <div className={getCurrentTab() === 'projects' ? '' : 'hidden'}>

          {/* Mobile: Current Job at the top */}
          <div className="block lg:hidden mb-6">
            {/* Current Job Card (mobile) */}
            <div className="card bg-base-100 shadow-lg border border-base-300 rounded-2xl overflow-hidden">
              <div className="card-body p-0 flex flex-row items-stretch min-h-[6rem]">
                {/* Left: Info */}
                <div className="flex-1 flex flex-col justify-center p-4 gap-2">
                  <div className="flex items-center gap-2 mb-1">
                    <FontAwesomeIcon icon={faHardHat} className="text-2xl text-primary" />
                    <span className="font-bold text-lg">Current Job</span>
                  </div>
                  <div>
                    <div className="font-semibold text-base">Downtown Tower A</div>
                    <div className="text-sm text-base-content/70">AT&T</div>
                    <div className="text-xs text-base-content/50">Clocked in: 8:00 AM</div>
                  </div>
                </div>
                {/* Divider */}
                <div className="w-px bg-base-200 my-4" />
                {/* Right: Button */}
                <div className="flex items-center justify-center p-0 bg-gradient-to-br from-primary/10 to-primary/20 w-1/2">
                  <button
                    className="btn btn-primary btn-lg text-lg font-semibold rounded-none border-none shadow-none transition-all flex items-center gap-2 w-full h-full justify-center"
                    style={{ minHeight: '100%' }}
                    onClick={() => handleSiteSelect(sites.find(s => s.siteName === 'Downtown Tower A')!)}
                  >
                    Go to Site
                    <FontAwesomeIcon icon={faChevronRight} className="text-xl" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Main + Sidebar Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-8">
            {/* Main Area: Search + Sites */}
            <div className="space-y-6">
              {/* Desktop: Current Job hidden, only show on mobile */}
              <div className="hidden lg:block">
                {/* Spacer for layout alignment */}
              </div>
              {/* Search Bar */}
              <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  <h2 className="card-title">
                    <FontAwesomeIcon icon={faSearch} className="text-primary" />
                    Search Sites
                  </h2>
                  <div className="form-control">
                    <input
                      type="text"
                      placeholder="Search sites..."
                      className="input input-bordered w-full"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              {/* All Sites List */}
              <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  <h2 className="card-title">
                    <FontAwesomeIcon icon={faBuilding} className="text-primary" />
                    All Sites
                  </h2>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {Object.entries(sitesByClient).map(([client, clientSites]) => (
                      <div key={client} className="space-y-2">
                        <h3 className="font-semibold text-sm text-base-content/70 border-b pb-1">{client}</h3>
                        {clientSites.map(site => (
                          <div 
                            key={site.id} 
                            className="p-3 bg-base-200 rounded-lg cursor-pointer hover:bg-base-300 transition-colors"
                            onClick={() => handleSiteSelect(site)}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h4 className="font-semibold text-sm">{site.siteName}</h4>
                                <p className="text-xs text-base-content/70">{site.projectType}</p>
                                <p className="text-xs text-base-content/60">{site.location}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="badge badge-outline badge-xs">{site.state}</span>
                                <FontAwesomeIcon icon={faChevronRight} className="text-xs text-base-content/50" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar: Info Cards */}
            <div className="space-y-6">
              {/* Desktop: Current Job Card in sidebar */}
              <div className="hidden lg:block">
                <div className="card bg-base-100 shadow-lg border border-base-300 rounded-2xl overflow-hidden">
                  <div className="card-body p-0 flex flex-row items-stretch min-h-[8rem]">
                    {/* Left: Info */}
                    <div className="flex-1 flex flex-col justify-center p-8 gap-2">
                      <div className="flex items-center gap-2 mb-1">
                        <FontAwesomeIcon icon={faHardHat} className="text-2xl text-primary" />
                        <span className="font-bold text-lg">Current Job</span>
                      </div>
                      <div>
                        <div className="font-semibold text-base">Downtown Tower A</div>
                        <div className="text-sm text-base-content/70">AT&T</div>
                        <div className="text-xs text-base-content/50">Clocked in: 8:00 AM</div>
                      </div>
                    </div>
                    {/* Divider */}
                    <div className="w-px bg-base-200 my-8" />
                    {/* Right: Button */}
                    <div className="flex items-center justify-center p-0 bg-gradient-to-br from-primary/10 to-primary/20 w-1/2">
                      <button
                        className="btn btn-primary btn-lg text-lg font-semibold rounded-none border-none shadow-none transition-all flex items-center gap-2 w-full h-full justify-center"
                        style={{ minHeight: '100%' }}
                        onClick={() => handleSiteSelect(sites.find(s => s.siteName === 'Downtown Tower A')!)}
                      >
                        Go to Site
                        <FontAwesomeIcon icon={faChevronRight} className="text-xl" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Outstanding Tasks Card */}
              <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  <h2 className="card-title">
                    <FontAwesomeIcon icon={faTasks} className="text-primary" />
                    Outstanding Tasks
                  </h2>
                  <div className="space-y-3">
                    {getAllMissingFiles(sites).length === 0 ? (
                      <div className="text-sm text-base-content/60">All required files are complete!</div>
                    ) : (
                      getAllMissingFiles(sites).map(({ siteName, file }, idx) => {
                        const site = sites.find(s => s.siteName === siteName);
                        return (
                          <button
                            key={file.id + siteName}
                            className="flex items-center justify-between p-2 bg-warning/10 rounded w-full text-left hover:bg-warning/20 transition cursor-pointer"
                            onClick={() => {
                              if (site) {
                                setSelectedSite(site);
                                setView({ view: 'siteView', siteId: site.id });
                                setTimeout(() => {
                                  handleOpenUploadModal(file);
                                }, 100); // slight delay to ensure site view renders
                              }
                            }}
                          >
                            <span className="text-sm">
                              <span className="font-semibold">{siteName}:</span> {file.name.replace(/_missing/i, '')}
                            </span>
                            <span className="badge badge-warning badge-sm">Missing</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
          

          
          {/* Management Tab - link to dedicated view (code split) */}
          {hasPermission('projectsManager') && (
            <div className={getCurrentTab() === 'management' ? '' : 'hidden'}>
              <div className="text-center py-12">
                <h2 className="text-2xl font-bold mb-4">Project Management</h2>
                <p className="text-base-content/70 mb-6">Open the project management tools in a dedicated view.</p>
                <button className="btn btn-primary" onClick={() => navigate('/projects/manage')}>Open Project Management</button>
              </div>
            </div>
          )}
        </div>

        {/* Site View Container */}
        <div className={view.view === 'siteView' ? '' : 'hidden'}>
          {selectedSite && (
            <>
              <SiteBreadcrumb
                client={selectedSite.client}
                siteName={selectedSite.siteName}
                onRootClick={handleBack}
                onClientClick={(clientName) => {
                  setView({ view: 'selection', tab: 'projects' });
                  setSearchTerm(clientName);
                }}
              />
              
              {/* Site Information */}
              <div className="card bg-base-100 shadow-xl mb-6">
                <div className="card-body p-4 lg:p-8 space-y-2 lg:space-y-4 text-sm lg:text-base">
                  <h2 className="card-title text-xl lg:text-2xl mb-2 lg:mb-4">{selectedSite.siteName}</h2>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-4">
                    {/* Left: Client & Project Type */}
                    <div className="space-y-2">
                      <div>
                        <span className="text-base-content/60">Client</span><br />
                        <span className="font-semibold">{selectedSite.client}</span>
                      </div>
                      <div>
                        <span className="text-base-content/60">Project Type</span><br />
                        <span className="font-semibold">{selectedSite.projectType}</span>
                      </div>
                    </div>
                    {/* Middle: Crew Lead & Total Hours */}
                    <div className="space-y-2">
                      <div>
                        <span className="text-base-content/60">Crew Lead</span><br />
                        <span className="font-semibold">{selectedSite.crewLead}</span>
                      </div>
                      <div>
                        <span className="text-base-content/60">Total Hours</span><br />
                        <span className="font-semibold text-lg font-mono">
                          {typeof selectedSite.totalHours === 'number' ? selectedSite.totalHours : 0}h
                        </span>
                      </div>
                    </div>
                    {/* Right: Location & Map */}
                    <div className="space-y-2 flex flex-col h-full">
                      <div>
                        <span className="text-base-content/60">Location</span><br />
                        <span className="font-semibold">{selectedSite.location}</span>
                      </div>
                      <div className="flex-1 flex items-center justify-center">
                        <div className="w-full h-40 lg:h-48 rounded-lg overflow-hidden shadow">
                          <iframe
                            title="Site Map"
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            loading="lazy"
                            allowFullScreen
                            src="https://www.openstreetmap.org/export/embed.html?bbox=-97.75%2C30.25%2C-97.70%2C30.30&layer=mapnik&marker=30.275%2C-97.725"
                          ></iframe>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced File Explorer */}
              <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* File Explorer */}
                    <div className="w-full">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <FontAwesomeIcon icon={faFolder} className="text-primary" />
                        File Structure
                      </h4>
                      <div className="bg-base-200 p-4 rounded-lg w-full">
                        <div className="text-lg lg:text-base">
                          <FileExplorer documents={selectedSite.documents} />
                        </div>
                      </div>
                    </div>
                    {/* Tasks Section */}
                    <div className="w-full">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <FontAwesomeIcon icon={faTasks} className="text-primary" />
                        Tasks
                      </h4>
                      <div className="mb-2 flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">Site Completion:</span>
                          <progress className="progress progress-primary w-32" value={getSiteCompletion(selectedSite)} max={100}></progress>
                          <span className="font-bold text-primary">{getSiteCompletion(selectedSite)}%</span>
                        </div>
                      </div>
                      <ul className="space-y-4">
                        {selectedSite.tasks?.map(task => {
                          const subTotal = task.subtasks?.length || 0
                          const subComplete = task.subtasks?.filter(st => st.completed).length || 0
                          const percent = subTotal === 0 ? (task.completed ? 100 : 0) : Math.round((subComplete / subTotal) * 100)
                          return (
                            <li key={task.id} className="bg-base-200 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-semibold">{task.name}</span>
                                <span className="text-xs text-base-content/60">{percent}%</span>
                              </div>
                              <progress className="progress progress-primary w-full mb-2" value={percent} max={100}></progress>
                              {task.subtasks && (
                                <ul className="ml-2 space-y-1">
                                  {task.subtasks.map(st => (
                                    <li key={st.id} className="flex items-center gap-2">
                                      <input type="checkbox" className="checkbox checkbox-xs" checked={st.completed} readOnly />
                                      <span className={st.completed ? 'line-through text-base-content/50' : ''}>{st.name}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          <SiteFileUploadModal
            open={uploadModalOpen}
            onClose={handleCloseUploadModal}
            missingFile={uploadModalFile}
            onUpload={handleUpload}
          />
        </div>


      </main>
      <Footer />
    </div>
  )
}

export default ProjectsPage 